'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ArrowRight, Menu, X } from 'lucide-react'
import { BuilderBuddiMark, BuilderBuddiMarkDark } from '@/components/brand/BuilderBuddiMark'
import { useRegion } from '@/contexts/marketing-region-context'
import { PRICING_REGIONS, REGION_CONFIGS } from '@/lib/billing/region'

const NAV_LINKS = [
  { href: '/#workflow', label: 'How it works' },
  { href: '/#features', label: 'Outcomes' },
  { href: '/#trades', label: 'Trades' },
  { href: '/pricing', label: 'Pricing' },
  { href: '/calculators', label: 'Estimators' },
  { href: '/blog', label: 'Blog' },
]

function RegionPicker({ solid }: { solid: boolean }) {
  const { region, setRegion, config } = useRegion()
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        aria-label="Select currency region"
        className={`flex items-center gap-1 text-[13px] font-medium px-2 py-1.5 rounded-lg transition-colors ${
          solid ? 'text-slate-600 hover:bg-slate-100' : 'text-white/65 hover:bg-white/10'
        }`}
      >
        <span>{config.flag}</span>
        <span>{config.currencyCode}</span>
        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" className="opacity-60">
          <path d="M2.5 3.5l2.5 3 2.5-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-1.5 z-50 w-48 rounded-xl border border-slate-200 bg-white shadow-xl py-1.5">
            {PRICING_REGIONS.map((r) => {
              const rc = REGION_CONFIGS[r]
              return (
                <button
                  key={r}
                  onClick={() => { setRegion(r); setOpen(false) }}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left hover:bg-slate-50 transition-colors ${
                    region === r ? 'font-semibold text-[#c2571a]' : 'text-slate-700'
                  }`}
                >
                  <span>{rc.flag}</span>
                  <span className="flex-1">{rc.label}</span>
                  <span className="text-[11px] text-slate-400">{rc.currencyCode}</span>
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

function trackCta(location: string) {
  try {
    fetch('/api/analytics/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event_name: 'marketing_cta_clicked', properties: { location, cta: 'start_free' } }),
    }).catch(() => {})
  } catch { /* ignore */ }
}

export function MarketingHeader() {
  const pathname = usePathname()
  const [scrolled, setScrolled] = useState(false)
  const [showStickyCta, setShowStickyCta] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const solid = scrolled || pathname !== '/'

  function resolveHref(href: string) {
    if (!href.startsWith('#') && !href.startsWith('/#')) return href
    if (href.startsWith('/#')) {
      const hash = href.slice(1) // '#features'
      // On the root page: pure hash scroll (no server request, middleware never fires)
      // Off the root page: include ?public=1 so middleware doesn't redirect logged-in users to /dashboard
      return pathname === '/' ? hash : `/?public=1${hash}`
    }
    return pathname === '/' ? href : `/${href}`
  }

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 24)
      setShowStickyCta(window.scrollY > window.innerHeight * 0.75)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <>
      <header
        className={`fixed left-0 right-0 z-50 transition-[background-color,box-shadow,border-color] duration-200 ${
          solid
            ? 'bg-white/98 shadow-sm border-b border-slate-200/80 backdrop-blur-md'
            : 'bg-transparent'
        }`}
        style={{ top: 'var(--bb-banner-h, 0px)' }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">

            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 shrink-0">
              {solid ? <BuilderBuddiMark size={26} /> : <BuilderBuddiMarkDark size={26} />}
              <span className={`text-[15px] font-bold tracking-tight transition-colors ${solid ? 'text-slate-900' : 'text-white'}`}>
                BuilderBuddi
              </span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={resolveHref(link.href)}
                  className={`text-[13.5px] font-medium px-3 py-2 rounded-lg transition-colors ${
                    solid
                      ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                      : 'text-white/80 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Desktop right actions */}
            <div className="hidden md:flex items-center gap-2 shrink-0">
              <RegionPicker solid={solid} />
              <Link
                href="/login"
                className={`text-[13.5px] font-semibold px-3.5 py-2 rounded-lg transition-colors ${
                  solid
                    ? 'text-slate-700 hover:text-slate-900 hover:bg-slate-100'
                    : 'text-white/80 hover:text-white hover:bg-white/10'
                }`}
              >
                Log in
              </Link>
              <Link
                href="/signup"
                className="inline-flex items-center gap-1.5 rounded-xl bg-[#c2571a] px-4 py-2 text-[13.5px] font-semibold text-white shadow-sm hover:bg-[#a84915] transition-colors"
                onClick={() => trackCta('header')}
              >
                Start Free
                <ArrowRight size={13} strokeWidth={2} aria-hidden />
              </Link>
            </div>

            {/* Mobile: Log in + hamburger */}
            <div className="flex md:hidden items-center gap-2">
              <Link
                href="/login"
                className={`text-[13px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                  solid ? 'text-slate-700 hover:bg-slate-100' : 'text-white/75 hover:bg-white/10'
                }`}
              >
                Log in
              </Link>
              <button
                className={`p-2 rounded-lg transition-colors ${solid ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                {menuOpen ? (
                  <X size={20} strokeWidth={2} aria-hidden />
                ) : (
                  <Menu size={20} strokeWidth={2} aria-hidden />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 shadow-lg">
            <div className="mx-auto max-w-7xl px-4 py-4 space-y-1">
              {NAV_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={resolveHref(link.href)}
                  className="block rounded-lg px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <Link
                  href="/signup"
                  className="flex items-center justify-center gap-2 rounded-xl bg-[#c2571a] px-4 py-3.5 text-[14px] font-semibold text-white hover:bg-[#a84915]"
                  onClick={() => { setMenuOpen(false); trackCta('mobile_menu') }}
                >
                  Start Free — No Card Required
                  <ArrowRight size={14} strokeWidth={2} aria-hidden />
                </Link>
                <Link
                  href="/login"
                  className="flex items-center justify-center rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  onClick={() => setMenuOpen(false)}
                >
                  Log in
                </Link>
                <Link
                  href="/website-intake"
                  className="flex items-center justify-center rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                  onClick={() => setMenuOpen(false)}
                >
                  Need a website?
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Sticky mobile bottom CTA — shown on mobile when not in mobile menu */}
      {!menuOpen && showStickyCta && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-t from-white via-white/95 to-transparent px-4 pb-[calc(env(safe-area-inset-bottom)+0.75rem)] pt-2 pointer-events-none md:hidden">
          <Link
            href="/signup"
            className="pointer-events-auto flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-[#c2571a] px-5 py-4 text-[15px] font-semibold text-white shadow-[0_8px_28px_rgba(194,87,26,0.45)] transition-transform active:scale-[0.98]"
            onClick={() => trackCta('mobile_sticky_cta')}
          >
            Start Free
            <ArrowRight size={15} strokeWidth={2} aria-hidden />
          </Link>
        </div>
      )}
    </>
  )
}
